import React, { Fragment, useEffect, useState } from 'react'
import { useAppSelector } from '../../hooks/redux'
import CartItem from '../../components/Cart/CartItem'
import styles from './cart.module.css'
import { TbCurrencyDram } from 'react-icons/tb'
import { FaCheckCircle } from 'react-icons/fa'
import { useAppDispatch } from '../../hooks/redux'
import { useCreateOrderMutation } from '../../features/order/orderApiSlice'
import { IProductOrderItem } from '../../interfaces'
import Notification from '../../components/Notification/Notification'
import { clearCart } from '../../features/cart/cartSlice'
import empty_cart from "../../imgs/empty_cart.png"
import { useTranslation } from "react-i18next"
import useScrollTop from "../../hooks/useScrollTop"
import { formatPhoneAM, rawPhone } from '../../utils/formatPhone'

const fmt = (n: number) => n.toLocaleString('ru-RU')

const Cart = () => {
    const { t, i18n } = useTranslation()
    const dispatch = useAppDispatch()
    const [phoneError, setPhoneError] = useState('')
    const [notification, setNotification] = useState({ open: false, message: '', type: '' })
    const [createOrder, { isLoading }] = useCreateOrderMutation()
    const { products } = useAppSelector(state => state.cart)
    const [phone, setPhone] = useState('')

    let subTotal = 0
    products.forEach(cp => {
        let sub = cp.count_original * cp.product.price_original + cp.count_copy * cp.product.price_copy
        if (cp.product.discount) sub -= Math.floor(cp.product.discount / 100 * sub)
        subTotal += sub
    })

    const showNotification = ({ type, message }: { type: string; message: string }) => {
        setNotification({ type, message, open: true })
        setTimeout(() => setNotification({ type: '', message: '', open: false }), 5000)
    }

    const makeOrder = async () => {
        const orderToPlace: Array<IProductOrderItem> = []
        products.forEach(cp => {
            const { product_id, count_copy, count_original } = cp
            if (count_original) orderToPlace.push({ product_id, count: count_original, product_type: 'original' })
            if (count_copy)     orderToPlace.push({ product_id, count: count_copy, product_type: 'copy' })
        })
        try {
            await createOrder({ order: orderToPlace, phone: rawPhone(phone) }).unwrap()
            dispatch(clearCart())
            showNotification({ type: 'success', message: t("cart.order_success") })
        } catch {
            showNotification({ type: 'error', message: t("errors.unknown_data") })
        }
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (/^0\d{8}$/.test(rawPhone(phone))) {
            setPhone('')
            makeOrder()
        } else {
            setPhoneError(t("cart.valid_phone_number") as string)
        }
    }

    useEffect(() => {
        const h = () => { if (phoneError) setPhoneError(t('cart.valid_phone_number') as string) }
        i18n.on('languageChanged', h)
        return () => { i18n.off('languageChanged', h) }
    }, [phoneError, t, i18n])

    useScrollTop()

    if (!products.length) {
        return (
            <div className={styles.empty_cart_container}>
                {notification.open && <Notification type={notification.type} message={notification.message} onClose={() => setNotification({ type:'', message:'', open:false })} />}
                <div className={styles.empty_cart_img}><img src={empty_cart} alt="" /></div>
            </div>
        )
    }

    return (
        <div className={`app_container ${styles.page}`}>
            {notification.open && <Notification type={notification.type} message={notification.message} onClose={() => setNotification({ type:'', message:'', open:false })} />}

            <h1 className={styles.title}>{t("general.cart")}</h1>

            <div className={styles.cart_card}>
                <div className={styles.cart_scroll}>
                    <table className={styles.cart_table}>
                        <thead>
                            <tr>
                                <th>{t("cart.products")}</th>
                                <th>{t("cart.item_price")}</th>
                                <th>{t("cart.discount")}</th>
                                <th>{t("cart.quantity")}</th>
                                <th>{t("cart.price")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map(product => {
                                const bothTypes = product.count_original && product.count_copy
                                if (bothTypes) return (
                                    <Fragment key={product.product_id + '_both'}>
                                        <CartItem cartProduct={{ cartItem: product, product_type: 'original' }} />
                                        <CartItem cartProduct={{ cartItem: product, product_type: 'copy' }} />
                                    </Fragment>
                                )
                                if (product.count_original) return <CartItem key={product.product_id + '_o'} cartProduct={{ cartItem: product, product_type: 'original' }} />
                                if (product.count_copy)     return <CartItem key={product.product_id + '_c'} cartProduct={{ cartItem: product, product_type: 'copy' }} />
                                return null
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={styles.summary}>
                <div className={styles.summary_row}>
                    <span>{t("cart.price")}</span>
                    <b>{fmt(subTotal)}&nbsp;<TbCurrencyDram /></b>
                </div>
                <form onSubmit={handleSubmit}>
                    <label className={styles.summary_label}>{t("inputs.your_phone_num")}</label>
                    <input
                        className={styles.summary_phone}
                        type="tel"
                        placeholder="(077) 56 56 56"
                        value={phone}
                        onChange={e => setPhone(formatPhoneAM(e.target.value))}
                    />
                    {phoneError && <p className={styles.field_error}>{phoneError}</p>}
                    <button className={styles.order_btn} disabled={isLoading}>
                        <FaCheckCircle /> {t("general.order")}
                    </button>
                </form>
            </div>
        </div>
    )
}

export default Cart
